import { useEffect, useEffectEvent, useMemo, type FC } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';

import useAvailableFlowListData from '../../../api/queries/useAvailableFlowListData.js';
import type { NewRequestFormValues } from '../../../types/newRequestForm.js';
import { OptionItem, ApproverEntity } from '../../../types/shared.js';
import SelectWithSearch from '../../SelectWithSearch.jsx';
import ApprovalFlowTabError from './ApprovalFlowTabError.jsx';
import ApprovalFlowTabSkeleton from './ApprovalFlowTabSkeleton.jsx';
import ApproversSection from './ApproversSection.jsx';

type Props = {
  isVisible: boolean;
};

const ApprovalFlowTab: FC<Props> = ({ isVisible }) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit_v2');
  const { control, setValue, trigger } = useFormContext<NewRequestFormValues>();
  const { field } = useController({ control, name: 'flowId' });

  const {
    data: flowListData,
    isPending: isFlowListPending,
    isError: isFlowListError,
  } = useAvailableFlowListData({
    enabled: isVisible,
  });

  const activeFlowList = useMemo(() => {
    if (!flowListData?.data?.items.length) {
      return [];
    }

    return flowListData.data.items.reduce<OptionItem[]>((acc, flow) => {
      acc.push({ label: flow.name, value: flow.id });
      return acc;
    }, []);
  }, [flowListData]);

  const firstActiveFlowId = activeFlowList[0]?.value ?? '';
  const hasOnlyOneFlow = activeFlowList.length === 1;

  const activeFlowEntityList = useMemo<ApproverEntity[]>(() => {
    const approval = flowListData?.data?.items.find(
      (flow) => flow.id === field.value,
    )?.approval;

    if (!approval) {
      return [];
    }

    return approval.map((item) => {
      if (item.type === 'roles') {
        return {
          id: item.id,
          label: item.role.role,
          type: 'role',
        };
      }

      if (item.type === 'operation') {
        return {
          id: item.id,
          label: item.operation,
          type: 'operation',
        };
      }

      return {
        id: item.id,
        label: item.name,
        type: 'user',
      };
    });
  }, [field.value, flowListData]);

  const handleFlowSelection = (option: OptionItem) => {
    field.onChange(option.value);
  };

  const populateFlowIdField = useEffectEvent(() => {
    setValue('flowId', firstActiveFlowId);
    trigger('flowId');
  });

  useEffect(() => {
    if (
      field.value ||
      !firstActiveFlowId ||
      field.value === firstActiveFlowId
    ) {
      return;
    }
    populateFlowIdField();
  }, [field.value, firstActiveFlowId]);

  if (isFlowListPending) {
    return <ApprovalFlowTabSkeleton />;
  }

  if (isFlowListError) {
    return <ApprovalFlowTabError />;
  }

  return (
    <div>
      {hasOnlyOneFlow ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <SelectWithSearch
                id="chosen-flow-select"
                label={t('Flow name')}
                a11yLabel="Select a flow"
                value={field.value}
                options={activeFlowList}
                onChange={handleFlowSelection}
                disabled
              />
            </div>
          </TooltipTrigger>
          <TooltipContent
            className="max-w-sm"
            side="top"
            align="start"
            arrow={false}
            sideOffset={-16}
          >
            {t('Only one approval flow is available for selection.')}
          </TooltipContent>
        </Tooltip>
      ) : (
        <SelectWithSearch
          id="chosen-flow-select"
          label={t('Flow name')}
          a11yLabel="Select a flow"
          value={field.value}
          options={activeFlowList}
          onChange={handleFlowSelection}
        />
      )}

      <div className="mt-4">
        <span
          className={typographyVariants({
            variant: 'label01',
            color: 'secondary',
            className: 'mb-3 block',
          })}
        >
          {t('Approvers')}
        </span>
        <ApproversSection approvers={activeFlowEntityList} />
      </div>
    </div>
  );
};

export default ApprovalFlowTab;
